// src/utils/quote/rasitusluokat.js
// Preset list + simplified rasitusluokka assistant for common Finnish concrete
// pours. NOT a substitute for structural design — final class must follow
// project drawings/specifications. Wire values match betoniRasitusLuokat.
// rasitusluokkaId rows, comma-joined. Display "shorthand" (e.g. "XC3-4, XF1")
// is for labels only.
//
// i18n: RASITUSLUOKKA_PRESETS / RASITUSLUOKKA_TREE are functions of `t` — the
// only consumers are wizard files (RasitusluokkaField, SpecGuideModal,
// SpecGuideResult, StepReview, betoniLaatuSelvitys.js) plus
// scripts/audit-rasitusluokka-tree.js, so converting to a function is fully
// contained (unlike quoteConstants.js's OPTIONS arrays, which have consumers
// outside this task). Only `label` / `meaning` / `example` / `group` /
// `question` / `description` / `resultLabel` / `help` go through `t()` — the
// `value` / `result` wire strings and node/option `id`s are DATA and stay
// untouched (they must match betoniRasitusLuokat rows and each other's `next`
// pointers exactly).

/**
 * `meaning` is one short sentence on what the class means in practice.
 * `example` is one tyypillinen kohde. Both shown as rich content inside the
 * RasitusluokkaField dropdown items so the codes aren't naked numbers.
 * @param {(key: string) => string} t
 */
export function rasitusluokkaPresets(t) {
    const g = {
        common: t("wizard.rasitusluokka.groups.common"),
        special: t("wizard.rasitusluokka.groups.special"),
        other: t("wizard.rasitusluokka.groups.other"),
    };
    return [
        { value: "",                       group: null,        label: t("wizard.rasitusluokka.presets.unknown.label") },

        { value: "X0",                     group: g.common,    label: t("wizard.rasitusluokka.presets.x0.label"),
            meaning: t("wizard.rasitusluokka.presets.x0.meaning"), example: t("wizard.rasitusluokka.presets.x0.example") },
        { value: "XC1",                    group: g.common,    label: t("wizard.rasitusluokka.presets.xc1.label"),
            meaning: t("wizard.rasitusluokka.presets.xc1.meaning"), example: t("wizard.rasitusluokka.presets.xc1.example") },
        { value: "XC2",                    group: g.common,    label: t("wizard.rasitusluokka.presets.xc2.label"),
            meaning: t("wizard.rasitusluokka.presets.xc2.meaning"), example: t("wizard.rasitusluokka.presets.xc2.example") },
        { value: "XC3, XC4, XF1",          group: g.common,    label: t("wizard.rasitusluokka.presets.xc34xf1.label"),
            meaning: t("wizard.rasitusluokka.presets.xc34xf1.meaning"), example: t("wizard.rasitusluokka.presets.xc34xf1.example") },
        { value: "XC3, XC4, XF3",          group: g.common,    label: t("wizard.rasitusluokka.presets.xc34xf3.label"),
            meaning: t("wizard.rasitusluokka.presets.xc34xf3.meaning"), example: t("wizard.rasitusluokka.presets.xc34xf3.example") },

        { value: "XC3",                    group: g.special,   label: t("wizard.rasitusluokka.presets.xc3Suojattu.label"),
            meaning: t("wizard.rasitusluokka.presets.xc3Suojattu.meaning"), example: t("wizard.rasitusluokka.presets.xc3Suojattu.example") },
        { value: "XC4",                    group: g.special,   label: t("wizard.rasitusluokka.presets.xc4Altis.label"),
            meaning: t("wizard.rasitusluokka.presets.xc4Altis.meaning"), example: t("wizard.rasitusluokka.presets.xc4Altis.example") },
        { value: "XD2",                    group: g.special,   label: t("wizard.rasitusluokka.presets.xd2.label"),
            meaning: t("wizard.rasitusluokka.presets.xd2.meaning"), example: t("wizard.rasitusluokka.presets.xd2.example") },
        { value: "XC3, XC4, XD1, XF2",     group: g.special,   label: t("wizard.rasitusluokka.presets.xc34xd1xf2.label"),
            meaning: t("wizard.rasitusluokka.presets.xc34xd1xf2.meaning"), example: t("wizard.rasitusluokka.presets.xc34xd1xf2.example") },
        { value: "XC3, XC4, XD2, XF2",     group: g.special,   label: t("wizard.rasitusluokka.presets.xc34xd2xf2.label"),
            meaning: t("wizard.rasitusluokka.presets.xc34xd2xf2.meaning"), example: t("wizard.rasitusluokka.presets.xc34xd2xf2.example") },
        { value: "XC3, XC4, XD3, XF4",     group: g.special,   label: t("wizard.rasitusluokka.presets.xc34xd3xf4.label"),
            meaning: t("wizard.rasitusluokka.presets.xc34xd3xf4.meaning"), example: t("wizard.rasitusluokka.presets.xc34xd3xf4.example") },
        { value: "XC3, XC4, XS1, XF2",     group: g.special,   label: t("wizard.rasitusluokka.presets.xc34xs1xf2.label"),
            meaning: t("wizard.rasitusluokka.presets.xc34xs1xf2.meaning"), example: t("wizard.rasitusluokka.presets.xc34xs1xf2.example") },
        { value: "XC3, XC4, XF4, XS3",     group: g.special,   label: t("wizard.rasitusluokka.presets.xc34xf4xs3.label"),
            meaning: t("wizard.rasitusluokka.presets.xc34xf4xs3.meaning"), example: t("wizard.rasitusluokka.presets.xc34xf4xs3.example") },
        { value: "XC2, XS2",               group: g.special,   label: t("wizard.rasitusluokka.presets.xc2xs2.label"),
            meaning: t("wizard.rasitusluokka.presets.xc2xs2.meaning"), example: t("wizard.rasitusluokka.presets.xc2xs2.example") },
        { value: "XA2, XC2",               group: g.special,   label: t("wizard.rasitusluokka.presets.xa2xc2.label"),
            meaning: t("wizard.rasitusluokka.presets.xa2xc2.meaning"), example: t("wizard.rasitusluokka.presets.xa2xc2.example") },

        { value: "Muu",                    group: g.other,     label: t("wizard.rasitusluokka.presets.muu.label") },
    ];
}

/**
 * Simplified tilausarvio decision tree — covers common Finnish small-pour
 * cases, NOT the full EN 206 / SFS 7022 / by65 selection space. Question nodes
 * have `question` + `options[]`, where each option's `next` points to another
 * question or a terminal node. Terminal nodes carry `terminal: true` plus the
 * resolved spec metadata (rasitusluokka wire string, label, minimum
 * lujuusluokka, cover {c50y, c100y} | null, description).
 *
 * Coverage gaps the tree does NOT auto-resolve (use manual RasitusluokkaField):
 *   - Chemical exposure (XA1/XA2/XA3) — depends on actual soil/groundwater
 *     chemistry; requires designer input.
 *
 * Cover values are indicative. The modal displays a disclaimer that final
 * rasitusluokka + cover come from project drawings.
 *
 * Source-of-truth design doc: docs/design/spec-tree.md.
 * @param {(key: string) => string} t
 */
export function rasitusluokkaTree(t) {
    const tr = "wizard.rasitusluokka.tree.";
    const tm = "wizard.rasitusluokka.terminals.";
    return {
        start: "sisaUlko",
        nodes: {
            // ---------- Question nodes ----------
            sisaUlko: {
                question: t(tr + "sisaUlko.question"),
                options: [
                    { id: "sisa", label: t(tr + "sisaUlko.options.sisa.label"), next: "raudoitettu",
                        description: t(tr + "sisaUlko.options.sisa.description") },
                    { id: "ulko", label: t(tr + "sisaUlko.options.ulko.label"), next: "pakkasrasitus",
                        description: t(tr + "sisaUlko.options.ulko.description") },
                ],
            },
            raudoitettu: {
                question: t(tr + "raudoitettu.question"),
                options: [
                    { id: "ei", label: t(tr + "raudoitettu.options.ei.label"), next: "term_X0",
                        description: t(tr + "raudoitettu.options.ei.description") },
                    { id: "kylla", label: t(tr + "raudoitettu.options.kylla.label"), next: "kosteustila",
                        description: t(tr + "raudoitettu.options.kylla.description") },
                ],
            },
            kosteustila: {
                question: t(tr + "kosteustila.question"),
                options: [
                    { id: "kuivaNormaaliSisa", label: t(tr + "kosteustila.options.kuivaNormaaliSisa.label"), next: "term_XC1",
                        description: t(tr + "kosteustila.options.kuivaNormaaliSisa.description") },
                    { id: "vaativaKostea", label: t(tr + "kosteustila.options.vaativaKostea.label"), next: "term_XC3_sisa",
                        description: t(tr + "kosteustila.options.vaativaKostea.description") },
                    { id: "kloridit", label: t(tr + "kosteustila.options.kloridit.label"), next: "term_XD2",
                        description: t(tr + "kosteustila.options.kloridit.description") },
                ],
            },
            pakkasrasitus: {
                question: t(tr + "pakkasrasitus.question"),
                options: [
                    { id: "ei", label: t(tr + "pakkasrasitus.options.ei.label"), next: "maanAllaTaiVedessa",
                        description: t(tr + "pakkasrasitus.options.ei.description") },
                    { id: "kylla", label: t(tr + "pakkasrasitus.options.kylla.label"), next: "kloridirasitusUlko",
                        description: t(tr + "pakkasrasitus.options.kylla.description") },
                ],
            },
            maanAllaTaiVedessa: {
                question: t(tr + "maanAllaTaiVedessa.question"),
                options: [
                    { id: "kylla", label: t(tr + "maanAllaTaiVedessa.options.kylla.label"), next: "term_XC2",
                        description: t(tr + "maanAllaTaiVedessa.options.kylla.description") },
                    { id: "ei", label: t(tr + "maanAllaTaiVedessa.options.ei.label"), next: "sateeltaSuojattu",
                        description: t(tr + "maanAllaTaiVedessa.options.ei.description") },
                ],
            },
            sateeltaSuojattu: {
                question: t(tr + "sateeltaSuojattu.question"),
                options: [
                    { id: "kylla", label: t(tr + "sateeltaSuojattu.options.kylla.label"), next: "term_XC3u",
                        description: t(tr + "sateeltaSuojattu.options.kylla.description") },
                    { id: "ei", label: t(tr + "sateeltaSuojattu.options.ei.label"), next: "term_XC4",
                        description: t(tr + "sateeltaSuojattu.options.ei.description") },
                ],
            },
            kloridirasitusUlko: {
                question: t(tr + "kloridirasitusUlko.question"),
                help: t(tr + "kloridirasitusUlko.help"),
                options: [
                    { id: "ei", label: t(tr + "kloridirasitusUlko.options.ei.label"), next: "pystyVaaka",
                        description: t(tr + "kloridirasitusUlko.options.ei.description") },
                    { id: "kylla", label: t(tr + "kloridirasitusUlko.options.kylla.label"), next: "kloridiLahde",
                        description: t(tr + "kloridirasitusUlko.options.kylla.description") },
                ],
            },
            pystyVaaka: {
                question: t(tr + "pystyVaaka.question"),
                options: [
                    { id: "pysty", label: t(tr + "pystyVaaka.options.pysty.label"), next: "term_XF1",
                        description: t(tr + "pystyVaaka.options.pysty.description") },
                    { id: "vaaka", label: t(tr + "pystyVaaka.options.vaaka.label"), next: "term_XF3",
                        description: t(tr + "pystyVaaka.options.vaaka.description") },
                ],
            },
            kloridiLahde: {
                question: t(tr + "kloridiLahde.question"),
                options: [
                    { id: "jaansulatussuola", label: t(tr + "kloridiLahde.options.jaansulatussuola.label"), next: "xdVoimakkuus",
                        description: t(tr + "kloridiLahde.options.jaansulatussuola.description") },
                    { id: "merivesi", label: t(tr + "kloridiLahde.options.merivesi.label"), next: "xsSijainti",
                        description: t(tr + "kloridiLahde.options.merivesi.description") },
                ],
            },
            xdVoimakkuus: {
                question: t(tr + "xdVoimakkuus.question"),
                options: [
                    { id: "lieva", label: t(tr + "xdVoimakkuus.options.lieva.label"), next: "term_XD1",
                        description: t(tr + "xdVoimakkuus.options.lieva.description") },
                    { id: "ankara", label: t(tr + "xdVoimakkuus.options.ankara.label"), next: "term_XD3",
                        description: t(tr + "xdVoimakkuus.options.ankara.description") },
                ],
            },
            xsSijainti: {
                question: t(tr + "xsSijainti.question"),
                options: [
                    { id: "ilmaSuola", label: t(tr + "xsSijainti.options.ilmaSuola.label"), next: "term_XS1",
                        description: t(tr + "xsSijainti.options.ilmaSuola.description") },
                    { id: "roiskealue", label: t(tr + "xsSijainti.options.roiskealue.label"), next: "term_XS3",
                        description: t(tr + "xsSijainti.options.roiskealue.description") },
                    { id: "vedenAlla", label: t(tr + "xsSijainti.options.vedenAlla.label"), next: "term_XS2",
                        description: t(tr + "xsSijainti.options.vedenAlla.description") },
                ],
            },

            // ---------- Terminal nodes ----------
            // lujuusluokkaMin values are rounded UP to the nearest betoniLujuus row.
            // C12/15 (X0) → C20/25; C32/40 (XD2 / XD1 / XS1) → C35/45.
            term_X0: {
                terminal: true, result: "X0", resultLabel: t(tm + "x0.resultLabel"),
                description: t(tm + "x0.description"), lujuusluokkaMin: "C20/25",
                // X0 cover depends on whether the structure is reinforced. For
                // unreinforced concrete it's N/A; for reinforced concrete in very
                // dry conditions the designer specifies. Either way, not for the
                // wizard to encode — render as "Rakennesuunnitelman mukaan".
                cover: null,
            },
            term_XC1: {
                terminal: true, result: "XC1", resultLabel: t(tm + "xc1.resultLabel"),
                description: t(tm + "xc1.description"), lujuusluokkaMin: "C20/25",
                cover: { c50y: 15, c100y: 25 },
            },
            term_XC3_sisa: {
                terminal: true, result: "XC3", resultLabel: t(tm + "xc3Sisa.resultLabel"),
                description: t(tm + "xc3Sisa.description"), lujuusluokkaMin: "C30/37",
                cover: { c50y: 25, c100y: 35 },
            },
            term_XD2: {
                terminal: true, result: "XD2", resultLabel: t(tm + "xd2.resultLabel"),
                description: t(tm + "xd2.description"), lujuusluokkaMin: "C35/45",
                cover: { c50y: 35, c100y: 45 },
            },
            term_XC2: {
                terminal: true, result: "XC2", resultLabel: t(tm + "xc2.resultLabel"),
                description: t(tm + "xc2.description"), lujuusluokkaMin: "C25/30",
                cover: { c50y: 25, c100y: 35 },
            },
            term_XC3u: {
                // Collapses with sisä-kostea per design — same wire string "XC3".
                terminal: true, result: "XC3", resultLabel: t(tm + "xc3u.resultLabel"),
                description: t(tm + "xc3u.description"), lujuusluokkaMin: "C30/37",
                cover: { c50y: 25, c100y: 35 },
            },
            term_XC4: {
                terminal: true, result: "XC4", resultLabel: t(tm + "xc4.resultLabel"),
                description: t(tm + "xc4.description"), lujuusluokkaMin: "C30/37",
                cover: { c50y: 30, c100y: 40 },
            },
            term_XF1: {
                terminal: true, result: "XC3, XC4, XF1", resultLabel: t(tm + "xf1.resultLabel"),
                description: t(tm + "xf1.description"), lujuusluokkaMin: "C30/37",
                cover: { c50y: 30, c100y: 40 },
            },
            term_XF3: {
                terminal: true, result: "XC3, XC4, XF3", resultLabel: t(tm + "xf3.resultLabel"),
                description: t(tm + "xf3.description"), lujuusluokkaMin: "C30/37",
                cover: { c50y: 30, c100y: 40 },
            },
            term_XD1: {
                terminal: true, result: "XC3, XC4, XD1, XF2", resultLabel: t(tm + "xd1.resultLabel"),
                description: t(tm + "xd1.description"), lujuusluokkaMin: "C35/45",
                cover: { c50y: 35, c100y: 45 },
            },
            term_XD3: {
                terminal: true, result: "XC3, XC4, XD3, XF4", resultLabel: t(tm + "xd3.resultLabel"),
                description: t(tm + "xd3.description"), lujuusluokkaMin: "C35/45",
                cover: { c50y: 45, c100y: 55 },
            },
            term_XS1: {
                terminal: true, result: "XC3, XC4, XS1, XF2", resultLabel: t(tm + "xs1.resultLabel"),
                description: t(tm + "xs1.description"), lujuusluokkaMin: "C35/45",
                cover: { c50y: 35, c100y: 45 },
            },
            term_XS2: {
                terminal: true, result: "XC2, XS2", resultLabel: t(tm + "xs2.resultLabel"),
                description: t(tm + "xs2.description"), lujuusluokkaMin: "C35/45",
                // Submerged structures are structural-design cases; cover comes
                // from project drawings, not a generic table.
                cover: null,
            },
            term_XS3: {
                // Tree-internal order is XS3 before XF4; canonicaliseRasitusluokka()
                // rewrites this to the historical preset order "XC3, XC4, XF4, XS3"
                // before persistence.
                terminal: true, result: "XC3, XC4, XS3, XF4", resultLabel: t(tm + "xs3.resultLabel"),
                description: t(tm + "xs3.description"), lujuusluokkaMin: "C35/45",
                cover: { c50y: 45, c100y: 55 },
            },
        },
    };
}

// One-way map for tree output → canonical RASITUSLUOKKA_PRESETS wire string.
// Tree produces pedagogically-ordered classes (XC → XD/XS → XF); presets use
// historical order. Apply this at every persistence boundary. Language-neutral
// (wire strings only) — no `t` needed.
const RESULT_CANONICAL_MAP = {
    "XC3, XC4, XS3, XF4": "XC3, XC4, XF4, XS3",
};

export function canonicaliseRasitusluokka(treeResult) {
    return RESULT_CANONICAL_MAP[treeResult] ?? treeResult;
}
